import React, { type JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthAppContext } from '../context/useAuthAppContext';
import { EdiRole } from '../types/UserInfos';
import { useLogger } from '../context/useLogger';
import PageLoadingComponent from './PageLoading';
import { AccountTabEnum } from '../pages/user-account/constants/account.constants';
import { AppRoutes } from '../types/appRoutes';
import { useUserInfos } from '../hooks/useUserInfos';

type RoleBasedRouteProps = {
  children: JSX.Element;
  allowedRoles?: EdiRole[]; // Liste des rôles autorisés
  notAllowedRoles?: EdiRole[]; // Liste des rôles non autorisés
};

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  allowedRoles,
  notAllowedRoles,
}) => {
  const { isLoading: isAuthLoading } = useAuth0();
  const { isAuthenticated, sessionStatus } = useAuthAppContext();
  const { isLoading: isLoadingUserInfos, hasRole, isRegistered, userInfos } = useUserInfos();
  const location = useLocation();
  const logger = useLogger();

  logger.debug('🚀 RoleBasedRoute current location:', location.pathname, {
    isAuthenticated,
    sessionStatus,
  });

  if (isAuthLoading || sessionStatus === 'checking' || isLoadingUserInfos) {
    return <PageLoadingComponent />;
  }

  if (!isAuthenticated || sessionStatus === 'invalid') {
    if (location.pathname === AppRoutes.routeLogin) {
      return children;
    }

    // Redirection vers /login, en mémorisant l'URL demandée pour y revenir après authentification
    return (
      <Navigate
        to={AppRoutes.routeLogin}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  const isRoleAllowed = hasRole(allowedRoles, true);
  const isRoleNotAllowed = hasRole(notAllowedRoles, false);
  const isRegisteredValue = isRegistered();

  logger.debug('RoleBasedRoute UserInfos:', JSON.stringify(userInfos));
  logger.debug('RoleBasedRoute UserInfos - isRoleAllowed:', isRoleAllowed);
  logger.debug('RoleBasedRoute UserInfos - isRoleNotAllowed:', isRoleNotAllowed);
  logger.debug('RoleBasedRoute UserInfos - isRegisteredValue:', isRegisteredValue);

  // Cas des rôles non authorisés pour un utilisateur connecté
  if (!isRoleAllowed || isRoleNotAllowed) {
    if (location.pathname === AppRoutes.routeUnauthorized) {
      return children;
    }

    // Redirection vers /unauthorized si non autorisé
    return <Navigate to={AppRoutes.routeUnauthorized} replace />;
  }

  // Cas des utilisateurs non référencés (pas de code Octave)
  if (!isRegisteredValue) {
    if (!location.pathname.startsWith(AppRoutes.routeUserAccount)) {
      // Redirection vers /register si non référencé
      return <Navigate to={`${AppRoutes.routeUserAccount}/${AccountTabEnum.Register}`} replace />;
    }

    return children;
  }

  return children;
};

export default RoleBasedRoute;
